import React, { useMemo, useRef, useState } from "react";
import { FiExternalLink, FiTool } from "react-icons/fi";
import AmbienteDetalhesDrawer from "../../components/AmbienteDetalhesDrawer";
import { EmptyStateErro, EmptyStateSemPlanilha } from "../../components/EmptyState";
import { BalaoComentarios } from "../../components/HistoricoAmbiente";
import BotaoExportacao from "../../components/Exportacao";
import NavegacaoEtapa from "../../components/NavegacaoEtapa";
import {
  ColumnResizeProvider,
  ResetColumnsButton,
  ResizableColgroup,
} from "../../components/ResizableColumns";
import { PageLoader } from "../../components/LoadingMarcenaria";
import SortableHeader from "../../components/SortableHeader";
import { useOrdenacaoTabela } from "../../hooks/useOrdenacaoTabela";
import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import { SoIcone } from "../../components/ListaCompacta/styles";
import CardsAmbientes, { dataCurta } from "./CardsAmbientes";
import { diasUteis, formatarData, formatarMoeda } from "../../utils/calculos";
import { hasValue, toDisplayText } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import {
  getDiasBloqueio,
  getLeadTimeMarcenariaProducao,
  getMarcenariaDeadline,
  isMarcenariaBlocked,
  countAllBlockedAmbientes,
  isMarcenariaReady,
} from "../../utils/marcenaria";
import { filtrarColunasPorPrecos } from "../../utils/colunas";
import { buildDeadlineStatus, compareDatesAsc } from "../../utils/status";
import { getTipoAmbienteSeguro } from "../../utils/tipoAmbiente";
import {
  Aba,
  Abas,
  EmptyText,
  FiltroToggleButton,
  NoticeCard,
  PageState,
  Secao,
  SecaoResumoContagem,
  SecaoTitulo,
  StatusBadge,
  SummaryCard,
  SummaryGrid,
  SummaryLabel,
  SummarySub,
  SummaryValue,
  TrAmbiente,
  Tabela,
  TabelaToolbar,
  TableWrapper,
  Td,
  Th,
  Titulo,
  TopBar,
  TopBarActions,
  Wrapper,
} from "./styles";

const EMPTY_VALUE = "\u2014";
// 2026-05-29 (F7): nova ordem [Todos, Fila, Em andamento, Bloqueados].
// Aba "Marceneiros" removida — ja existe pagina dedicada /marceneiros
// com analise mais completa. "Todos" combina FILA + ANDAMENTO +
// BLOQUEADOS numa unica tabela com badge de origem.
const ABAS = [
  { id: "todos", label: "Todos" },
  { id: "fila", label: "Fila" },
  { id: "andamento", label: "Em andamento" },
  { id: "bloqueados", label: "Bloqueados" },
];

// Tones do badge de origem em "Todos" — sinalizacao visual rapida da fila
// de cada ambiente. Reuso de tones existentes em StatusBadge.
const ORIGEM_LABELS = {
  FILA: "Fila",
  ANDAMENTO: "Em andamento",
  BLOQUEADO: "Bloqueado",
};
const ORIGEM_TONES = {
  FILA: "default",
  ANDAMENTO: "info",
  BLOQUEADO: "warning",
};

function buildTodosRows(fila, andamento, bloqueados) {
  const combined = [
    ...(fila || []).map((row) => ({ ...row, _origem: "FILA" })),
    ...(andamento || []).map((row) => ({ ...row, _origem: "ANDAMENTO" })),
    ...(bloqueados || []).map((row) => ({ ...row, _origem: "BLOQUEADO" })),
  ];
  // Ordena por prazo de marcenaria ASC (mais proximo do vencimento primeiro).
  return combined.sort((left, right) =>
    compareDatesAsc(left.dataLimiteMarcenaria, right.dataLimiteMarcenaria)
  );
}

const CAMPOS_CRITICOS = [
  F.DATA_DE_CORTE,
  F.DATA_DE_BORDO,
  F.DATA_DE_FURACAO,
  F.DATA_DE_USINAGEM,
  F.MARCENARIA_INICIO,
  F.MARCENARIA_TERMINO,
  F.MARCENEIRO_RESPONSAVEL,
  F.APROVACAO_DO_EXECUTIVO,
  F.FINAL_MARCENARIA,
  F.TIPO_AMBIENTE,
];

function compareText(left, right) {
  return String(left ?? "").localeCompare(String(right ?? ""), "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
}

function getSafeDiasUteis(start, end) {
  const value = diasUteis(start, end);
  return value != null && value >= 0 ? value : null;
}

function formatDays(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return EMPTY_VALUE;
  }

  return `${new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 1,
  }).format(Number(value))} du`;
}

function average(values) {
  const validValues = values.filter((value) => value != null && Number.isFinite(Number(value)));
  if (!validValues.length) {
    return null;
  }

  return validValues.reduce((total, value) => total + Number(value), 0) / validValues.length;
}

function getStatus(row) {
  const deadline = row.dataLimiteMarcenaria;

  if (!deadline) {
    return {
      status: "Sem prazo",
      statusVariant: "warning",
    };
  }

  return buildDeadlineStatus(deadline, {
    overdueLabel: "Prazo vencido",
    todayLabel: "Vence hoje",
    warningLabel: "Vence esta semana",
    fallbackLabel: "No prazo",
  });
}

function sortFila(left, right) {
  const byDeadline = compareDatesAsc(left.dataLimiteMarcenaria, right.dataLimiteMarcenaria);
  if (byDeadline !== 0) {
    return byDeadline;
  }

  const byApprovalDelay = (right.diasDesdeAprovacao ?? -1) - (left.diasDesdeAprovacao ?? -1);
  if (byApprovalDelay !== 0) {
    return byApprovalDelay;
  }

  const byLote = compareText(left.numeroLote, right.numeroLote);
  return byLote !== 0 ? byLote : left.index - right.index;
}

function sortAndamento(left, right) {
  const byDeadline = compareDatesAsc(left.dataLimiteMarcenaria, right.dataLimiteMarcenaria);
  if (byDeadline !== 0) {
    return byDeadline;
  }

  const byInicio = compareDatesAsc(left.marcenariaInicio, right.marcenariaInicio);
  return byInicio !== 0 ? byInicio : left.index - right.index;
}

function buildOpenRow(row, index) {
  const dataLimiteMarcenaria = getMarcenariaDeadline(row);
  const item = {
    index,
    // Linha crua: abre o painel do ambiente (datas + histórico de comentários).
    rowPlanilha: row,
    cliente: toDisplayText(row[F.NOME_DO_CLIENTE], "Cliente não informado"),
    ambiente: toDisplayText(row[F.AMBIENTE], "Ambiente não informado"),
    tipo: getTipoAmbienteSeguro(row),
    numeroLote: toDisplayText(row[F.NUMERO_DO_LOTE], EMPTY_VALUE),
    marceneiro: toDisplayText(row[F.MARCENEIRO_RESPONSAVEL], EMPTY_VALUE),
    valor: row[F.VALOR_DO_AMBIENTE],
    aprovacaoExecutivo: row[F.APROVACAO_DO_EXECUTIVO],
    finalMarcenaria: row[F.FINAL_MARCENARIA],
    finalMarcenariaReprogramado: row[F.FINAL_MARCENARIA_REPROGRAMADO],
    dataLimiteMarcenaria,
    marcenariaInicio: row[F.MARCENARIA_INICIO],
    marcenariaTermino: row[F.MARCENARIA_TERMINO],
    finalMaquinas: row[F.FINAL_MAQUINAS],
    inicioBloqueio: row[F.INICIO_DO_BLOQUEIO],
    finalBloqueio: row[F.FINAL_DO_BLOQUEIO],
    bloqueado: isMarcenariaBlocked(row),
    diasBloqueado: getDiasBloqueio(row),
    diasDesdeAprovacao: getSafeDiasUteis(row[F.APROVACAO_DO_EXECUTIVO], new Date()),
    // Dias úteis aguardando inicio da marcenaria, a partir do fim das máquinas.
    diasNaFila: hasValue(row[F.FINAL_MAQUINAS])
      ? getSafeDiasUteis(row[F.FINAL_MAQUINAS], new Date())
      : null,
    diasEmMarcenaria: hasValue(row[F.MARCENARIA_INICIO])
      ? getLeadTimeMarcenariaProducao(row, new Date())
      : null,
  };

  return {
    ...item,
    ...getStatus(item),
  };
}

function buildLeadRows(dados) {
  const grouped = new Map();

  dados.forEach((row) => {
    if (
      !isMarcenariaReady(row) ||
      !hasValue(row[F.MARCENEIRO_RESPONSAVEL]) ||
      !hasValue(row[F.MARCENARIA_INICIO]) ||
      !hasValue(row[F.MARCENARIA_TERMINO])
    ) {
      return;
    }

    const marceneiro = String(row[F.MARCENEIRO_RESPONSAVEL]).trim();
    const tipo = getTipoAmbienteSeguro(row);
    const leadTime = getLeadTimeMarcenariaProducao(row, row[F.MARCENARIA_TERMINO]);
    const diasBloqueado = getDiasBloqueio(row, {
      periodStart: row[F.MARCENARIA_INICIO],
      periodEnd: row[F.MARCENARIA_TERMINO],
    });

    if (leadTime == null) {
      return;
    }

    const key = `${marceneiro}||${tipo}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        marceneiro,
        tipo,
        leads: [],
      });
    }

    grouped.get(key).leads.push(leadTime);
    if (diasBloqueado != null) {
      if (!grouped.get(key).bloqueios) {
        grouped.get(key).bloqueios = [];
      }
      grouped.get(key).bloqueios.push(diasBloqueado);
    }
  });

  return [...grouped.values()]
    .map((group) => ({
      marceneiro: group.marceneiro,
      tipo: group.tipo,
      qtd: group.leads.length,
      leadTimeMedio: average(group.leads),
      bloqueioMedio: average(group.bloqueios || []),
      menorLead: Math.min(...group.leads),
      maiorLead: Math.max(...group.leads),
    }))
    .sort((left, right) => {
      const byMarceneiro = compareText(left.marceneiro, right.marceneiro);
      return byMarceneiro !== 0 ? byMarceneiro : compareText(left.tipo, right.tipo);
    });
}

function buildMarceneiroRows(abertos, leadRows) {
  const grouped = new Map();

  abertos.forEach((row) => {
    if (row.marceneiro === EMPTY_VALUE) {
      return;
    }

    if (!grouped.has(row.marceneiro)) {
      grouped.set(row.marceneiro, {
        marceneiro: row.marceneiro,
        fila: 0,
        emAndamento: 0,
        bloqueados: 0,
        atrasados: 0,
        tipos: new Set(),
      });
    }

    const group = grouped.get(row.marceneiro);
    group.tipos.add(row.tipo);

    if (row.bloqueado) {
      group.bloqueados += 1;
    } else if (hasValue(row.marcenariaInicio)) {
      group.emAndamento += 1;
    } else {
      group.fila += 1;
    }

    if (row.statusVariant === "danger") {
      group.atrasados += 1;
    }
  });

  return [...grouped.values()]
    .map((group) => {
      const leads = leadRows
        .filter((row) => row.marceneiro === group.marceneiro)
        .flatMap((row) => Array(row.qtd).fill(row.leadTimeMedio));

      return {
        marceneiro: group.marceneiro,
        fila: group.fila,
        emAndamento: group.emAndamento,
        bloqueados: group.bloqueados,
        total: group.fila + group.emAndamento + group.bloqueados,
        atrasados: group.atrasados,
        tipos: group.tipos.size,
        leadTimeMedio: average(leads),
      };
    })
    .sort((left, right) => right.total - left.total || compareText(left.marceneiro, right.marceneiro));
}

function StatusCell({ row }) {
  return <StatusBadge $tone={row.statusVariant}>{row.status}</StatusBadge>;
}

function MoneyCell({ value, precosVisiveis }) {
  return precosVisiveis ? formatarMoeda(value) : "******";
}

const marceneiroMeta = (row) => (row.marceneiro === EMPTY_VALUE ? null : row.marceneiro);
const diasMeta = (valor) => (valor == null ? null : formatDays(valor));
const seloStatus = (row) => <StatusCell row={row} />;
const barraStatus = (row) => row.statusVariant;

// 2026-05-29 (F7): mapeia cada columnKey ao valor real da linha, para
// a ordenacao clicavel (useOrdenacaoTabela). Cobre os campos de todas
// as 4 tabelas (Fila/Andamento/Bloqueados/Todos) — as keys sao
// consistentes entre elas. Datas retornam Date (ordenacao cronologica),
// dias retornam number, demais retornam string.
const VALOR_POR_KEY = {
  origem: (r) => r._origem,
  cliente: (r) => r.cliente,
  ambiente: (r) => r.ambiente,
  tipo: (r) => r.tipo,
  lote: (r) => r.numeroLote,
  marceneiro: (r) => r.marceneiro,
  prazo: (r) => r.dataLimiteMarcenaria,
  inicio: (r) => r.marcenariaInicio,
  inicioBlq: (r) => r.inicioBloqueio,
  diasFila: (r) => r.diasNaFila,
  diasMarceneiro: (r) => r.diasEmMarcenaria,
  diasBlq: (r) => r.diasBloqueado,
  status: (r) => r.status,
  valor: (r) => r.valor,
};
const getValorOrdenacao = (row, key) => VALOR_POR_KEY[key]?.(row);

const COLS_FILA = [
  { key: "cliente", defaultWidth: null },
  { key: "ambiente", defaultWidth: null },
  { key: "tipo", defaultWidth: "90px" },
  { key: "lote", defaultWidth: "80px" },
  { key: "prazo", defaultWidth: "110px" },
  { key: "diasFila", defaultWidth: "90px" },
  { key: "status", defaultWidth: "100px" },
  { key: "valor", defaultWidth: "100px" },
];

function FilaTable({ rows, precosVisiveis, compacto, onAbrir }) {
  const cols = useMemo(
    () => filtrarColunasPorPrecos(COLS_FILA, precosVisiveis),
    [precosVisiveis],
  );
  const { linhas, sort, toggle } = useOrdenacaoTabela(rows, getValorOrdenacao);
  if (compacto) {
    return (
      <CardsAmbientes
        rows={linhas}
        onAbrir={onAbrir}
        testId="marcenaria-fila-cards"
        selo={seloStatus}
        barra={barraStatus}
        meta={(row) => [
          ["lote", row.numeroLote],
          ["tipo", row.tipo],
          ["prazo", dataCurta(row.dataLimiteMarcenaria)],
          ["fila", diasMeta(row.diasNaFila)],
        ]}
        vazio="Nenhum ambiente apto aguardando início da marcenaria."
      />
    );
  }
  return (
    <ColumnResizeProvider tableId="marcenaria-fila" columns={cols} persistirNoBackend>
    <TabelaToolbar>
      <ResetColumnsButton />
    </TabelaToolbar>
    <TableWrapper>
      <Tabela $wide $density="compact">
        <ResizableColgroup />
        <thead>
          <tr>
            <SortableHeader as={Th} columnKey="cliente" sort={sort} onSort={toggle}>Cliente</SortableHeader>
            <SortableHeader as={Th} columnKey="ambiente" sort={sort} onSort={toggle}>Ambiente</SortableHeader>
            <SortableHeader as={Th} columnKey="tipo" sort={sort} onSort={toggle}>Tipo</SortableHeader>
            <SortableHeader as={Th} columnKey="lote" sort={sort} onSort={toggle}>Lote</SortableHeader>
            <SortableHeader as={Th} columnKey="prazo" sort={sort} onSort={toggle}>Prazo marcenaria</SortableHeader>
            <SortableHeader as={Th} columnKey="diasFila" sort={sort} onSort={toggle} $center align="center">Dias em fila</SortableHeader>
            <SortableHeader as={Th} columnKey="status" sort={sort} onSort={toggle} $center align="center">Status</SortableHeader>
            {precosVisiveis && (
              <SortableHeader as={Th} columnKey="valor" sort={sort} onSort={toggle} $right align="right">Valor</SortableHeader>
            )}
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <Td colSpan={precosVisiveis ? 8 : 7} $center>
                Nenhum ambiente apto aguardando início da marcenaria.
              </Td>
            </tr>
          )}
          {linhas.map((row) => (
            <TrAmbiente key={`fila-${row.index}`} onClick={() => onAbrir(row)}>
              <Td>{row.cliente}</Td>
              <Td>
                {row.ambiente}
                <BalaoComentarios row={row.rowPlanilha} />
              </Td>
              <Td>{row.tipo}</Td>
              <Td>{row.numeroLote}</Td>
              <Td>{formatarData(row.dataLimiteMarcenaria)}</Td>
              <Td $center>{formatDays(row.diasNaFila)}</Td>
              <Td $center>
                <StatusCell row={row} />
              </Td>
              {precosVisiveis && (
                <Td $right>
                  <MoneyCell value={row.valor} precosVisiveis={precosVisiveis} />
                </Td>
              )}
            </TrAmbiente>
          ))}
        </tbody>
      </Tabela>
    </TableWrapper>
    </ColumnResizeProvider>
  );
}

const COLS_ANDAMENTO = [
  { key: "cliente", defaultWidth: null },
  { key: "ambiente", defaultWidth: null },
  { key: "lote", defaultWidth: "80px" },
  { key: "marceneiro", defaultWidth: "120px" },
  { key: "inicio", defaultWidth: "100px" },
  { key: "prazo", defaultWidth: "100px" },
  { key: "diasMarceneiro", defaultWidth: "120px" },
  { key: "status", defaultWidth: "100px" },
  { key: "valor", defaultWidth: "100px" },
];

function AndamentoTable({ rows, precosVisiveis, compacto, onAbrir }) {
  const cols = useMemo(
    () => filtrarColunasPorPrecos(COLS_ANDAMENTO, precosVisiveis),
    [precosVisiveis],
  );
  const { linhas, sort, toggle } = useOrdenacaoTabela(rows, getValorOrdenacao);
  if (compacto) {
    return (
      <CardsAmbientes
        rows={linhas}
        onAbrir={onAbrir}
        testId="marcenaria-andamento-cards"
        selo={seloStatus}
        barra={barraStatus}
        meta={(row) => [
          ["lote", row.numeroLote],
          ["marc.", marceneiroMeta(row)],
          ["início", dataCurta(row.marcenariaInicio)],
          ["prazo", dataCurta(row.dataLimiteMarcenaria)],
          ["dias", diasMeta(row.diasEmMarcenaria)],
        ]}
        vazio="Nenhum ambiente em andamento na marcenaria."
      />
    );
  }
  return (
    <ColumnResizeProvider tableId="marcenaria-andamento" columns={cols} persistirNoBackend>
    <TabelaToolbar>
      <ResetColumnsButton />
    </TabelaToolbar>
    <TableWrapper>
      <Tabela $wide $density="compact">
        <ResizableColgroup />
        <thead>
          <tr>
            <SortableHeader as={Th} columnKey="cliente" sort={sort} onSort={toggle}>Cliente</SortableHeader>
            <SortableHeader as={Th} columnKey="ambiente" sort={sort} onSort={toggle}>Ambiente</SortableHeader>
            <SortableHeader as={Th} columnKey="lote" sort={sort} onSort={toggle}>Lote</SortableHeader>
            <SortableHeader as={Th} columnKey="marceneiro" sort={sort} onSort={toggle}>Marceneiro</SortableHeader>
            <SortableHeader as={Th} columnKey="inicio" sort={sort} onSort={toggle}>Início</SortableHeader>
            <SortableHeader as={Th} columnKey="prazo" sort={sort} onSort={toggle}>Prazo marcenaria</SortableHeader>
            <SortableHeader as={Th} columnKey="diasMarceneiro" sort={sort} onSort={toggle} $center align="center">Dias com o marceneiro</SortableHeader>
            <SortableHeader as={Th} columnKey="status" sort={sort} onSort={toggle} $center align="center">Status</SortableHeader>
            {precosVisiveis && (
              <SortableHeader as={Th} columnKey="valor" sort={sort} onSort={toggle} $right align="right">Valor</SortableHeader>
            )}
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <Td colSpan={precosVisiveis ? 9 : 8} $center>
                Nenhum ambiente em andamento na marcenaria.
              </Td>
            </tr>
          )}
          {linhas.map((row) => (
            <TrAmbiente key={`andamento-${row.index}`} onClick={() => onAbrir(row)}>
              <Td>{row.cliente}</Td>
              <Td>
                {row.ambiente}
                <BalaoComentarios row={row.rowPlanilha} />
              </Td>
              <Td>{row.numeroLote}</Td>
              <Td $muted={row.marceneiro === EMPTY_VALUE}>{row.marceneiro}</Td>
              <Td>{formatarData(row.marcenariaInicio)}</Td>
              <Td>{formatarData(row.dataLimiteMarcenaria)}</Td>
              <Td $center>{formatDays(row.diasEmMarcenaria)}</Td>
              <Td $center>
                <StatusCell row={row} />
              </Td>
              {precosVisiveis && (
                <Td $right>
                  <MoneyCell value={row.valor} precosVisiveis={precosVisiveis} />
                </Td>
              )}
            </TrAmbiente>
          ))}
        </tbody>
      </Tabela>
    </TableWrapper>
    </ColumnResizeProvider>
  );
}

const COLS_BLOQUEADOS = [
  { key: "cliente", defaultWidth: null },
  { key: "ambiente", defaultWidth: null },
  { key: "lote", defaultWidth: "80px" },
  { key: "marceneiro", defaultWidth: "120px" },
  { key: "inicioBlq", defaultWidth: "100px" },
  { key: "diasBlq", defaultWidth: "100px" },
  { key: "prazo", defaultWidth: "100px" },
  { key: "status", defaultWidth: "100px" },
  { key: "valor", defaultWidth: "100px" },
];

function BloqueadosTable({ rows, precosVisiveis, compacto, onAbrir }) {
  const cols = useMemo(
    () => filtrarColunasPorPrecos(COLS_BLOQUEADOS, precosVisiveis),
    [precosVisiveis],
  );
  const { linhas, sort, toggle } = useOrdenacaoTabela(rows, getValorOrdenacao);
  if (compacto) {
    return (
      <CardsAmbientes
        rows={linhas}
        onAbrir={onAbrir}
        testId="marcenaria-bloqueados-cards"
        selo={() => <StatusBadge $tone="warning">Bloqueado</StatusBadge>}
        barra={() => "warning"}
        meta={(row) => [
          ["lote", row.numeroLote],
          ["marc.", marceneiroMeta(row)],
          ["bloqueio", dataCurta(row.inicioBloqueio)],
          ["dias", diasMeta(row.diasBloqueado)],
          ["prazo", dataCurta(row.dataLimiteMarcenaria)],
        ]}
        vazio="Nenhum ambiente está com bloqueio aberto."
      />
    );
  }
  return (
    <ColumnResizeProvider tableId="marcenaria-bloqueados" columns={cols} persistirNoBackend>
    <TabelaToolbar>
      <ResetColumnsButton />
    </TabelaToolbar>
    <TableWrapper>
      <Tabela $wide $density="compact">
        <ResizableColgroup />
        <thead>
          <tr>
            <SortableHeader as={Th} columnKey="cliente" sort={sort} onSort={toggle}>Cliente</SortableHeader>
            <SortableHeader as={Th} columnKey="ambiente" sort={sort} onSort={toggle}>Ambiente</SortableHeader>
            <SortableHeader as={Th} columnKey="lote" sort={sort} onSort={toggle}>Lote</SortableHeader>
            <SortableHeader as={Th} columnKey="marceneiro" sort={sort} onSort={toggle}>Marceneiro</SortableHeader>
            <SortableHeader as={Th} columnKey="inicioBlq" sort={sort} onSort={toggle}>Início bloqueio</SortableHeader>
            <SortableHeader as={Th} columnKey="diasBlq" sort={sort} onSort={toggle} $center align="center">Dias bloqueado</SortableHeader>
            <SortableHeader as={Th} columnKey="prazo" sort={sort} onSort={toggle}>Prazo marcenaria</SortableHeader>
            <SortableHeader as={Th} columnKey="status" sort={sort} onSort={toggle} $center align="center">Status</SortableHeader>
            {precosVisiveis && (
              <SortableHeader as={Th} columnKey="valor" sort={sort} onSort={toggle} $right align="right">Valor</SortableHeader>
            )}
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <Td colSpan={precosVisiveis ? 9 : 8} $center>
                Nenhum ambiente está com bloqueio aberto.
              </Td>
            </tr>
          )}
          {linhas.map((row) => (
            <TrAmbiente key={`bloqueado-${row.index}`} onClick={() => onAbrir(row)}>
              <Td>{row.cliente}</Td>
              <Td>
                {row.ambiente}
                <BalaoComentarios row={row.rowPlanilha} />
              </Td>
              <Td>{row.numeroLote}</Td>
              <Td $muted={row.marceneiro === EMPTY_VALUE}>{row.marceneiro}</Td>
              <Td>{formatarData(row.inicioBloqueio)}</Td>
              <Td $center>{formatDays(row.diasBloqueado)}</Td>
              <Td>{formatarData(row.dataLimiteMarcenaria)}</Td>
              <Td $center>
                <StatusBadge $tone="warning">Bloqueado</StatusBadge>
              </Td>
              {precosVisiveis && (
                <Td $right>
                  <MoneyCell value={row.valor} precosVisiveis={precosVisiveis} />
                </Td>
              )}
            </TrAmbiente>
          ))}
        </tbody>
      </Tabela>
    </TableWrapper>
    </ColumnResizeProvider>
  );
}

const COLS_TODOS = [
  { key: "origem", defaultWidth: "110px" },
  { key: "cliente", defaultWidth: null },
  { key: "ambiente", defaultWidth: null },
  { key: "tipo", defaultWidth: "90px" },
  { key: "lote", defaultWidth: "80px" },
  { key: "marceneiro", defaultWidth: "120px" },
  { key: "inicio", defaultWidth: "100px" },
  { key: "prazo", defaultWidth: "110px" },
  { key: "status", defaultWidth: "100px" },
  { key: "valor", defaultWidth: "100px" },
];

function TodosTable({ rows, precosVisiveis, compacto, onAbrir }) {
  const cols = useMemo(
    () => filtrarColunasPorPrecos(COLS_TODOS, precosVisiveis),
    [precosVisiveis],
  );
  const { linhas, sort, toggle } = useOrdenacaoTabela(rows, getValorOrdenacao);
  if (compacto) {
    return (
      <CardsAmbientes
        rows={linhas}
        onAbrir={onAbrir}
        testId="marcenaria-todos-cards"
        selo={seloStatus}
        barra={barraStatus}
        meta={(row) => [
          ["etapa", ORIGEM_LABELS[row._origem] || row._origem],
          ["lote", row.numeroLote],
          ["marc.", marceneiroMeta(row)],
          ["início", dataCurta(row.marcenariaInicio)],
          ["prazo", dataCurta(row.dataLimiteMarcenaria)],
        ]}
        vazio="Nenhum ambiente em marcenaria."
      />
    );
  }
  return (
    <ColumnResizeProvider tableId="marcenaria-todos" columns={cols} persistirNoBackend>
    <TabelaToolbar>
      <ResetColumnsButton />
    </TabelaToolbar>
    <TableWrapper>
      <Tabela $wide $density="compact">
        <ResizableColgroup />
        <thead>
          <tr>
            <SortableHeader as={Th} columnKey="origem" sort={sort} onSort={toggle} $center align="center">Fila</SortableHeader>
            <SortableHeader as={Th} columnKey="cliente" sort={sort} onSort={toggle}>Cliente</SortableHeader>
            <SortableHeader as={Th} columnKey="ambiente" sort={sort} onSort={toggle}>Ambiente</SortableHeader>
            <SortableHeader as={Th} columnKey="tipo" sort={sort} onSort={toggle}>Tipo</SortableHeader>
            <SortableHeader as={Th} columnKey="lote" sort={sort} onSort={toggle}>Lote</SortableHeader>
            <SortableHeader as={Th} columnKey="marceneiro" sort={sort} onSort={toggle}>Marceneiro</SortableHeader>
            <SortableHeader as={Th} columnKey="inicio" sort={sort} onSort={toggle}>Início</SortableHeader>
            <SortableHeader as={Th} columnKey="prazo" sort={sort} onSort={toggle}>Prazo marcenaria</SortableHeader>
            <SortableHeader as={Th} columnKey="status" sort={sort} onSort={toggle} $center align="center">Status</SortableHeader>
            {precosVisiveis && (
              <SortableHeader as={Th} columnKey="valor" sort={sort} onSort={toggle} $right align="right">Valor</SortableHeader>
            )}
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <Td colSpan={precosVisiveis ? 10 : 9} $center>
                Nenhum ambiente em marcenaria.
              </Td>
            </tr>
          )}
          {linhas.map((row) => (
            <TrAmbiente key={`todos-${row._origem}-${row.index}`} onClick={() => onAbrir(row)}>
              <Td $center>
                <StatusBadge $tone={ORIGEM_TONES[row._origem] || "default"}>
                  {ORIGEM_LABELS[row._origem] || row._origem}
                </StatusBadge>
              </Td>
              <Td>{row.cliente}</Td>
              <Td>
                {row.ambiente}
                <BalaoComentarios row={row.rowPlanilha} />
              </Td>
              <Td>{row.tipo}</Td>
              <Td>{row.numeroLote}</Td>
              <Td $muted={row.marceneiro === EMPTY_VALUE}>{row.marceneiro}</Td>
              <Td>{formatarData(row.marcenariaInicio)}</Td>
              <Td>{formatarData(row.dataLimiteMarcenaria)}</Td>
              <Td $center>
                <StatusCell row={row} />
              </Td>
              {precosVisiveis && (
                <Td $right>
                  <MoneyCell value={row.valor} precosVisiveis={precosVisiveis} />
                </Td>
              )}
            </TrAmbiente>
          ))}
        </tbody>
      </Tabela>
    </TableWrapper>
    </ColumnResizeProvider>
  );
}

export default function Marcenaria() {
  const { dados, erro, initialLoading } = useDados();
  const { precosVisiveis } = usePrecosVisiveis();
  const pageRef = useRef(null);
  const compacto = useMediaQuery(mq.tablet);
  const [abaAtiva, setAbaAtiva] = useState("todos");
  // Clique na linha/card abre o painel padrão do ambiente (datas + histórico).
  const [ambienteAberto, setAmbienteAberto] = useState(null);
  const abrirAmbiente = (row) =>
    setAmbienteAberto({
      ambiente: row.rowPlanilha[F.AMBIENTE],
      lote: row.rowPlanilha[F.NUMERO_DO_LOTE] ?? "—",
      row: row.rowPlanilha,
    });
  // Filtros de visibilidade por categoria do marceneiro responsavel.
  // Quando ligados, ocultam da tela os ambientes cujo marceneiro tem
  // "EMPREITA" / "TERCEIRIZADO" no nome.
  const [ocultarEmpreita, setOcultarEmpreita] = useState(false);
  const [ocultarTerceirizado, setOcultarTerceirizado] = useState(false);

  const camposFaltantes = useMemo(() => {
    if (!dados.length) {
      return [];
    }

    const sample = dados[0] || {};
    return CAMPOS_CRITICOS.filter((field) => !Object.prototype.hasOwnProperty.call(sample, field));
  }, [dados]);

  const analise = useMemo(() => {
    const sample = dados[0] || {};
    const bloqueioDisponivel =
      Object.prototype.hasOwnProperty.call(sample, F.INICIO_DO_BLOQUEIO) &&
      Object.prototype.hasOwnProperty.call(sample, F.FINAL_DO_BLOQUEIO);
    let abertos = dados
      .map(buildOpenRow)
      .filter((row, index) => isMarcenariaReady(dados[index]) && !hasValue(row.marcenariaTermino));

    // Filtros de visibilidade (toggles na TopBar): ocultam ambientes por
    // categoria do marceneiro responsavel sem alterar os dados de origem.
    // Aplicados aqui na fonte para que todas as listas derivadas (fila,
    // andamento, bloqueados, todos, marceneiros) e os KPIs fiquem coerentes.
    if (ocultarEmpreita) {
      abertos = abertos.filter((row) => !/EMPREITA/i.test(row.marceneiro || ""));
    }
    if (ocultarTerceirizado) {
      abertos = abertos.filter((row) => !/TERCEIRIZAD/i.test(row.marceneiro || ""));
    }

    const bloqueados = abertos.filter((row) => row.bloqueado).sort((left, right) =>
      compareDatesAsc(left.inicioBloqueio, right.inicioBloqueio)
    );
    const fila = abertos
      .filter((row) => !row.bloqueado && !hasValue(row.marcenariaInicio))
      .sort(sortFila);
    const andamento = abertos
      .filter((row) => !row.bloqueado && hasValue(row.marcenariaInicio))
      .sort(sortAndamento);
    const leadRows = buildLeadRows(dados);
    const marceneiros = buildMarceneiroRows(abertos, leadRows);

    // C-018: contagem total de bloqueios na planilha (em qualquer etapa).
    // O card "Bloqueados" da tela de marcenaria so mostra os que ja entraram
    // na marcenaria; este total adicional revela bloqueios pre-marcenaria
    // que estavam invisiveis ao gestor.
    const bloqueadosTotalPlanilha = countAllBlockedAmbientes(dados);
    const bloqueadosForaMarcenaria = Math.max(
      bloqueadosTotalPlanilha - bloqueados.length,
      0
    );

    // Bug fix B7: aba "Todos" combina fila + andamento + bloqueados em
    // uma unica lista ordenada por prazo de marcenaria, com badge de
    // origem em cada linha (FILA/ANDAMENTO/BLOQUEADO).
    const todos = buildTodosRows(fila, andamento, bloqueados);

    return {
      bloqueioDisponivel,
      abertos,
      fila,
      andamento,
      bloqueados,
      bloqueadosTotalPlanilha,
      bloqueadosForaMarcenaria,
      leadRows,
      marceneiros,
      todos,
      semMarceneiro: abertos.filter((row) => row.marceneiro === EMPTY_VALUE).length,
      atrasados: abertos.filter((row) => row.statusVariant === "danger").length,
    };
  }, [dados, ocultarEmpreita, ocultarTerceirizado]);

  // Snapshot para o agendador (chamado antes dos early returns).
  const exportSnapshotPayload = useMemo(() => {
    if (!analise || !analise.abertos) return null;
    const colunasAmbiente = [
      { key: "cliente",       label: "Cliente",     type: "text" },
      { key: "ambiente",      label: "Ambiente",    type: "text" },
      { key: "tipo",          label: "Tipo",        type: "text" },
      { key: "numeroLote",    label: "Lote",        type: "text" },
      { key: "marceneiro",    label: "Marceneiro",  type: "text" },
      { key: "aprovacaoExecutivo", label: "Aprovado em", type: "date" },
      { key: "marcenariaInicio",   label: "Início",       type: "date" },
      { key: "dataLimiteMarcenaria", label: "Prazo",      type: "date" },
      { key: "diasDesdeAprovacao", label: "Dias da aprov.", type: "days" },
      { key: "diasEmMarcenaria",   label: "Em marcenaria",  type: "days" },
      { key: "status",        label: "Status",      type: "status" },
      { key: "valor",         label: "Valor",       type: "currency" },
    ];
    const colunasMarceneiros = [
      { key: "marceneiro",  label: "Marceneiro", type: "text" },
      { key: "total",       label: "Total amb.", type: "number" },
      { key: "fila",        label: "Fila",       type: "number" },
      { key: "emAndamento", label: "Em andamento", type: "number" },
      { key: "bloqueados",  label: "Bloqueados", type: "number" },
      { key: "atrasados",   label: "Atrasados",  type: "number" },
    ];
    return {
      titulo: "Marcenaria",
      // Gate do agendador — não publica snapshot do 1º render vazio
      // (Excel/CSV agendado saía só com Resumo zerado).
      // Gate do agendador. `!initialLoading` sozinho nao distingue "carregou"
      // de "falhou": em todo caminho de erro do DadosContext o estado vira
      // dados=[] + erro + loading=false, entao o snapshot saia "pronto" com
      // ZERO linhas — e o Excel/PDF agendado chegava vazio na pasta, sem aviso.
      // E o cenario mais provavel no iframe, que nao tem gesto para pedir
      // permissao do arquivo da planilha.
      pronto: !initialLoading && !erro && dados.length > 0,
      resumo: [
        { label: "Na Marcenaria",   value: analise.abertos.length,   type: "number" },
        { label: "Fila",            value: analise.fila.length,      type: "number" },
        { label: "Em andamento",    value: analise.andamento.length, type: "number" },
        { label: "Bloqueados",      value: analise.bloqueados.length, type: "number" },
        { label: "Atrasados",       value: analise.atrasados,        type: "number" },
      ],
      secoes: [
        { id: "fila",       label: "Fila",         columns: colunasAmbiente,   rows: analise.fila },
        { id: "andamento",  label: "Em andamento", columns: colunasAmbiente,   rows: analise.andamento },
        { id: "bloqueados", label: "Bloqueados",   columns: colunasAmbiente,   rows: analise.bloqueados },
        {
          id: "marceneiros",
          label: "Marceneiros",
          columns: colunasMarceneiros,
          rows: (analise.marceneiros || []).map((m) => ({
            marceneiro: m.nome,
            total: m.total,
            fila: m.fila,
            emAndamento: m.emAndamento,
            bloqueados: m.bloqueados,
            atrasados: m.atrasados,
          })),
        },
        {
          id: "todos",
          label: "Todos (Fila + Andamento + Bloqueados)",
          columns: [
            { key: "_origem", label: "Fila", type: "text" },
            ...colunasAmbiente,
          ],
          rows: (analise.todos || []).map((row) => ({
            ...row,
            _origem: ORIGEM_LABELS[row._origem] || row._origem,
          })),
        },
      ],
      visual: {
        ref: pageRef,
        sections: [
          { id: "resumo", label: "Cards de resumo", selector: "[data-pdf='resumo-marcenaria']" },
          { id: "visao",  label: "Aba atual",       selector: "[data-pdf='visao-marcenaria']" },
        ],
      },
    };
  }, [analise, initialLoading, erro, dados.length]);
  useExportSnapshot("marcenaria", exportSnapshotPayload, pageRef);

  if (initialLoading) {
    return <PageLoader scope="marcenaria" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  if (!dados.length) {
    return <EmptyStateSemPlanilha />;
  }

  if (camposFaltantes.length > 0) {
    return (
      <PageState $tone="error">
        Dados críticos ausentes para separar a Marcenaria: {camposFaltantes.join(", ")}.
      </PageState>
    );
  }

  // Dados estruturados para exportação. As 4 abas viram 4 seções
  // (Fila, Em andamento, Bloqueados, Marceneiros).
  const colunasAmbiente = [
    { key: "cliente",       label: "Cliente",     type: "text" },
    { key: "ambiente",      label: "Ambiente",    type: "text" },
    { key: "tipo",          label: "Tipo",        type: "text" },
    { key: "numeroLote",    label: "Lote",        type: "text" },
    { key: "marceneiro",    label: "Marceneiro",  type: "text" },
    { key: "aprovacaoExecutivo", label: "Aprovado em", type: "date" },
    { key: "marcenariaInicio",   label: "Início",       type: "date" },
    { key: "dataLimiteMarcenaria", label: "Prazo",      type: "date" },
    { key: "diasDesdeAprovacao", label: "Dias da aprov.", type: "days" },
    { key: "diasEmMarcenaria",   label: "Em marcenaria",  type: "days" },
    { key: "status",        label: "Status",      type: "status" },
    { key: "valor",         label: "Valor",       type: "currency" },
  ];

  const colunasMarceneiros = [
    { key: "marceneiro",  label: "Marceneiro", type: "text" },
    { key: "total",       label: "Total amb.", type: "number" },
    { key: "fila",        label: "Fila",       type: "number" },
    { key: "emAndamento", label: "Em andamento", type: "number" },
    { key: "bloqueados",  label: "Bloqueados", type: "number" },
    { key: "atrasados",   label: "Atrasados",  type: "number" },
  ];

  const exportResumo = [
    { label: "Na Marcenaria",  value: analise.abertos.length, type: "number" },
    { label: "Fila",           value: analise.fila.length,    type: "number" },
    { label: "Em andamento",   value: analise.andamento.length, type: "number" },
    { label: "Bloqueados (Marcenaria)", value: analise.bloqueados.length, type: "number" },
    { label: "Bloqueios em outras etapas", value: analise.bloqueadosForaMarcenaria, type: "number" },
    { label: "Atrasados",      value: analise.atrasados,      type: "number" },
    { label: "Sem marceneiro", value: analise.semMarceneiro,  type: "number" },
  ];

  // Bug fix B7: coluna _origem (FILA/ANDAMENTO/BLOQUEADO) na frente das
  // demais para a aba "Todos" exportada (PDF/Excel/CSV).
  const colunasTodos = [
    { key: "_origem", label: "Fila", type: "text" },
    ...colunasAmbiente,
  ];

  const exportSecoes = [
    { id: "fila",        label: "Fila",          columns: colunasAmbiente, rows: analise.fila },
    { id: "andamento",   label: "Em andamento",  columns: colunasAmbiente, rows: analise.andamento },
    { id: "bloqueados",  label: "Bloqueados",    columns: colunasAmbiente, rows: analise.bloqueados },
    {
      id: "marceneiros",
      label: "Marceneiros",
      columns: colunasMarceneiros,
      rows: (analise.marceneiros || []).map((m) => ({
        marceneiro: m.nome,
        total: m.total,
        fila: m.fila,
        emAndamento: m.emAndamento,
        bloqueados: m.bloqueados,
        atrasados: m.atrasados,
      })),
    },
    {
      id: "todos",
      label: "Todos (Fila + Andamento + Bloqueados)",
      columns: colunasTodos,
      rows: (analise.todos || []).map((row) => ({
        ...row,
        _origem: ORIGEM_LABELS[row._origem] || row._origem,
      })),
    },
  ];

  const exportVisual = {
    ref: pageRef,
    sections: [
      { id: "resumo", label: "Cards de resumo", selector: "[data-pdf='resumo-marcenaria']" },
      { id: "visao",  label: "Aba atual",       selector: "[data-pdf='visao-marcenaria']" },
    ],
  };

  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <NavegacaoEtapa>
          <Titulo>Marcenaria</Titulo>
        </NavegacaoEtapa>
        <TopBarActions>
          <FiltroToggleButton
            type="button"
            $active={ocultarEmpreita}
            aria-pressed={ocultarEmpreita}
            title={`Empreitas — ${ocultarEmpreita ? "mostrar" : "ocultar"}`}
            aria-label={`Empreitas — ${
              ocultarEmpreita ? "mostrar" : "ocultar"
            } ambientes de empreita`}
            onClick={() => setOcultarEmpreita((valor) => !valor)}
          >
            <FiTool size={16} aria-hidden="true" />
          </FiltroToggleButton>
          <FiltroToggleButton
            type="button"
            $active={ocultarTerceirizado}
            aria-pressed={ocultarTerceirizado}
            title={`Terceirizados — ${
              ocultarTerceirizado ? "mostrar" : "ocultar"
            }`}
            aria-label={`Terceirizados — ${
              ocultarTerceirizado ? "mostrar" : "ocultar"
            } ambientes terceirizados`}
            onClick={() => setOcultarTerceirizado((valor) => !valor)}
          >
            <FiExternalLink size={16} aria-hidden="true" />
          </FiltroToggleButton>
          <SoIcone>
            <BotaoExportacao
              titulo="Marcenaria"
              resumo={exportResumo}
              secoes={exportSecoes}
              visual={exportVisual}
            />
          </SoIcone>
        </TopBarActions>
      </TopBar>

      <SummaryGrid data-pdf="resumo-marcenaria">
        <SummaryCard>
          <SummaryLabel>Na Marcenaria</SummaryLabel>
          <SummaryValue>{analise.abertos.length}</SummaryValue>
          <SummarySub>aptos e ainda sem término</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Fila</SummaryLabel>
          <SummaryValue>{analise.fila.length}</SummaryValue>
          <SummarySub>aptos sem início</SummarySub>
        </SummaryCard>
        <SummaryCard>
          <SummaryLabel>Em andamento</SummaryLabel>
          <SummaryValue>{analise.andamento.length}</SummaryValue>
          <SummarySub>início informado, sem término</SummarySub>
        </SummaryCard>
        <SummaryCard $tone={analise.bloqueados.length > 0 ? "warning" : "success"}>
          <SummaryLabel>Bloqueados (na Marcenaria)</SummaryLabel>
          <SummaryValue $tone={analise.bloqueados.length > 0 ? "warning" : "success"}>
            {analise.bloqueados.length}
          </SummaryValue>
          <SummarySub>início de bloqueio sem final</SummarySub>
        </SummaryCard>
        <SummaryCard
          $tone={analise.bloqueadosForaMarcenaria > 0 ? "warning" : "success"}
        >
          <SummaryLabel>Bloqueios em outras etapas</SummaryLabel>
          <SummaryValue
            $tone={analise.bloqueadosForaMarcenaria > 0 ? "warning" : "success"}
          >
            {analise.bloqueadosForaMarcenaria}
          </SummaryValue>
          <SummarySub>
            ambientes pré-marcenaria com bloqueio aberto (total {analise.bloqueadosTotalPlanilha})
          </SummarySub>
        </SummaryCard>
        <SummaryCard $tone={analise.atrasados > 0 ? "danger" : "success"}>
          <SummaryLabel>Atrasados</SummaryLabel>
          <SummaryValue $tone={analise.atrasados > 0 ? "danger" : "success"}>
            {analise.atrasados}
          </SummaryValue>
          <SummarySub>com prazo de marcenaria vencido</SummarySub>
        </SummaryCard>
        <SummaryCard $tone={analise.semMarceneiro > 0 ? "warning" : "success"}>
          <SummaryLabel>Sem marceneiro</SummaryLabel>
          <SummaryValue $tone={analise.semMarceneiro > 0 ? "warning" : "success"}>
            {analise.semMarceneiro}
          </SummaryValue>
          <SummarySub>campo responsável vazio</SummarySub>
        </SummaryCard>
      </SummaryGrid>

      <Abas>
        {ABAS.map((aba) => (
          <Aba
            key={aba.id}
            type="button"
            $active={abaAtiva === aba.id}
            onClick={() => setAbaAtiva(aba.id)}
          >
            {aba.label}
          </Aba>
        ))}
      </Abas>

      <Secao data-pdf="visao-marcenaria" data-export-expand>
        {abaAtiva === "fila" && (
          <>
            <SecaoTitulo>Fila da Marcenaria</SecaoTitulo>
            <FilaTable onAbrir={abrirAmbiente} rows={analise.fila} precosVisiveis={precosVisiveis} compacto={compacto} />
          </>
        )}

        {abaAtiva === "andamento" && (
          <>
            <SecaoTitulo>Ambientes em andamento</SecaoTitulo>
            <AndamentoTable onAbrir={abrirAmbiente} rows={analise.andamento} precosVisiveis={precosVisiveis} compacto={compacto} />
          </>
        )}

        {abaAtiva === "bloqueados" && (
          <>
            <SecaoTitulo>Bloqueados</SecaoTitulo>
            {!analise.bloqueioDisponivel ? (
              <>
                <NoticeCard $tone="warning">
                  A visão Bloqueados está preparada para as colunas INICIO DO BLOQUEIO e
                  FINAL DO BLOQUEIO, mas esses campos ainda não chegaram pela API local.
                </NoticeCard>
                <EmptyText>Nenhum bloqueio foi classificado por texto livre.</EmptyText>
              </>
            ) : (
              <BloqueadosTable onAbrir={abrirAmbiente} rows={analise.bloqueados} precosVisiveis={precosVisiveis} compacto={compacto} />
            )}
          </>
        )}

        {abaAtiva === "todos" && (
          <>
            <SecaoTitulo>
              Todos os ambientes na marcenaria
              <SecaoResumoContagem>
                ({analise.fila.length} em fila · {analise.andamento.length} em andamento · {analise.bloqueados.length} bloqueados)
              </SecaoResumoContagem>
            </SecaoTitulo>
            <TodosTable onAbrir={abrirAmbiente} rows={analise.todos} precosVisiveis={precosVisiveis} compacto={compacto} />
          </>
        )}
      </Secao>
      <AmbienteDetalhesDrawer ambiente={ambienteAberto} onClose={() => setAmbienteAberto(null)} />
    </Wrapper>
  );
}
